import type { TabularSheetCellValue } from '../../shared/tabular-sheet-payload.model';
import { DWT_DEFRA_SUBMISSION_STATUS_CODE_TO_SHEET_LABEL } from '../dwt-defra-submissions.constants';
import { isDwtPayloadReportStatusCode } from '../../dwt-payloads/dwt-payloads.constants';
import type { DwtPayloadValidationResult } from '../../dwt-payloads/utils/parse-dwt-payload-validation.util';
import { parseDwtPayloadResponsePayload } from '../../dwt-payloads/utils/parse-dwt-payload-validation.util';
import type { DwtDefraSubmissionSheetRow } from '../models/dwt-defra-submissions-sheet.model';
import {
    flattenDefraSubmissionFields,
    type DefraSubmissionField,
} from './flatten-defra-submission-fields.util';

function parseJsonIfString(value: unknown): unknown {
    if (typeof value !== 'string') {
        return value;
    }
    const trimmed = value.trim();
    if (trimmed === '') {
        return value;
    }
    try {
        return JSON.parse(trimmed);
    } catch {
        return value;
    }
}

function asRecord(value: unknown): Record<string, unknown> | null {
    if (value === null || typeof value !== 'object' || Array.isArray(value)) {
        return null;
    }
    return value as Record<string, unknown>;
}

function readString(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }
    if (value == null) {
        return '';
    }
    return String(value);
}

/** Submission timestamp from the record, kept as UTC (`toISOString`). */
function toUtcDateTime(value: unknown): string {
    const raw = readString(value).trim();
    if (raw === '') {
        return '';
    }
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) {
        return raw;
    }
    return parsed.toISOString();
}

function readSubmissionStatusLabel(value: unknown): string {
    const code = Number(value);
    if (Number.isInteger(code) && isDwtPayloadReportStatusCode(code)) {
        return DWT_DEFRA_SUBMISSION_STATUS_CODE_TO_SHEET_LABEL[code];
    }
    return readString(value);
}

/** `wasteItems[0].physicalForm` and `wasteItems.0.physicalForm` identify the same field. */
export function normalizeDefraFieldKey(key: string): string {
    return key
        .trim()
        .replace(/\[(\d+)\]/g, '.$1')
        .replace(/\.+/g, '.')
        .replace(/^\./, '')
        .replace(/\.$/, '');
}

function buildValidationMessagesByKey(
    validation: DwtPayloadValidationResult
): Map<string, string[]> {
    const messages = new Map<string, string[]>();

    const addMessage = (key: string, message: string): void => {
        const normalizedKey = normalizeDefraFieldKey(key);

        if (!normalizedKey) {
            return;
        }

        const existing = messages.get(normalizedKey) ?? [];
        existing.push(message);

        messages.set(normalizedKey, existing);
    };

    for (const error of validation.errors) {
        addMessage(error.key, `Error: ${error.message}`);
    }

    for (const warning of validation.warnings) {
        addMessage(warning.key, `Warning: ${warning.message}`);
    }

    return messages;
}

function unmatchedFieldRows(
    validation: DwtPayloadValidationResult,
    matchedKeys: ReadonlySet<string>
): { key: string; text: string }[] {
    const byKey = new Map<string, string[]>();
    const remember = (key: string, line: string): void => {
        const normalized = normalizeDefraFieldKey(key);
        if (normalized === '' || matchedKeys.has(normalized)) {
            return;
        }
        const existing = byKey.get(key) ?? [];
        existing.push(line);
        byKey.set(key, existing);
    };
    for (const error of validation.errors) {
        remember(error.key, `Error: ${error.message}`);
    }
    for (const warning of validation.warnings) {
        remember(warning.key, `Warning: ${warning.message}`);
    }
    return Array.from(byKey.entries(), ([key, lines]) => ({
        key,
        text: lines.join('\n'),
    }));
}

function sheetRow(
    meta: Omit<DwtDefraSubmissionSheetRow, 'name' | 'value' | 'fieldErrorsAndWarnings'>,
    name: string,
    value: TabularSheetCellValue,
    fieldErrorsAndWarnings: string
): DwtDefraSubmissionSheetRow {
    return {
        ...meta,
        name,
        value,
        fieldErrorsAndWarnings,
    };
}

/**
 * One Get-RegulatoryReports record becomes one row per field in `transformedRequestPayload`
 * (the JSON Trace sent to DEFRA). Submission status and timestamp are repeated on every row.
 */
export function mapDwtDefraSubmissionApiRecordToRows(
    record: unknown
): DwtDefraSubmissionSheetRow[] {
    const row = asRecord(record);
    const validation = parseDwtPayloadResponsePayload(parseJsonIfString(row?.['responsePayload']));
    const fields: DefraSubmissionField[] = flattenDefraSubmissionFields(
        row?.['transformedRequestPayload']
    );
    const meta = {
        wasteTrackingId: readString(row?.['wasteTrackingId']),
        submissionDateTime: toUtcDateTime(row?.['processedAt']),
        submissionStatus: readSubmissionStatusLabel(row?.['status']),
        yourUniqueReference: readString(row?.['yourReference']),
    };
    const matchedKeys = new Set(fields.map((field) => normalizeDefraFieldKey(field.name)));
    // Build validation messages only once.
    const validationMessages = buildValidationMessagesByKey(validation);

    const fieldRows = fields.map((field) =>
        sheetRow(
            meta,
            field.name,
            field.value,
            validationMessages.get(normalizeDefraFieldKey(field.name))?.join('\n') ?? ''
        )
    );

    const extraRows = unmatchedFieldRows(validation, matchedKeys).map((item) =>
        sheetRow(meta, item.key, '', item.text)
    );

    const message = validation.message?.trim() ?? '';

    const messageRow = message === '' ? [] : [sheetRow(meta, '', '', message)];

    const rows = [...fieldRows, ...extraRows, ...messageRow];

    return rows.length > 0 ? rows : [sheetRow(meta, '', '', '')];
}
